import { combineLatest, map, Observable } from 'rxjs';
import { fields$ } from 'streams/fields/fields$';
import { dashboardFiltersSuggestions$ } from 'dashboard/dashboardFiltersSuggestions$';
import {
  DashboardBuilderWidget,
  DashboardFilter,
  getDashboardFiltersLinkCountDict,
  MinimalFieldRepresentation,
  toDashboardFilter,
  toUnlinkedDashboardFilter,
} from '@ardoq/dashboard';
import { startWith } from 'rxjs/operators';
import { REPORT_DEFAULT_FIELDS } from '@ardoq/report-reader';
import { UnlinkedDashboardFilter } from '@ardoq/dashboard';
import {
  AnyWidgetAttributes,
  APIDashboardFilterParameter,
  APIFieldAttributes,
  APIFieldType,
  DashboardFilterValues,
} from '@ardoq/api-types';
import { i18nLocale$ } from '../../../streams/i18nLocale$';

export const fieldsByName$ = fields$.pipe(
  map(
    ({
      byName: fieldsByName,
    }): Record<string, MinimalFieldRepresentation | APIFieldAttributes> => ({
      ...Object.fromEntries<{
        label: string;
        name: string;
        type: APIFieldType;
      }>(fieldsByName.entries()),
      ...REPORT_DEFAULT_FIELDS.reduce(
        (byName, { label, name, type }) => ({
          ...byName,
          [name]: { name, label, type },
        }),
        {}
      ),
    })
  )
);

export type DashboardFilter$State = {
  dashboardFilters: Array<UnlinkedDashboardFilter | DashboardFilter>;
  fieldsByName: Record<string, MinimalFieldRepresentation | APIFieldAttributes>;
};

export const getDashboardFilter$ = (
  dashboardData$: Observable<{
    widgets: AnyWidgetAttributes[] | DashboardBuilderWidget[];
    globalFilterParameters: APIDashboardFilterParameter[] | undefined;
    globalFilterValues: DashboardFilterValues | undefined;
  }>
): Observable<DashboardFilter$State> =>
  combineLatest({
    fieldsByName: fieldsByName$,
    dashboardData: dashboardData$,
    suggestions: dashboardFiltersSuggestions$,
    i18nLocale: i18nLocale$,
  }).pipe(
    map(
      ({
        fieldsByName,
        dashboardData: {
          globalFilterParameters = [],
          globalFilterValues,
          widgets,
        },
        suggestions,
        i18nLocale,
      }): DashboardFilter$State => {
        const linkCountsAndWarningsByParameterId =
          getDashboardFiltersLinkCountDict(globalFilterParameters, widgets);
        return {
          dashboardFilters: globalFilterParameters.map(parameter => {
            const parameterLinkCount =
              linkCountsAndWarningsByParameterId[parameter.id]?.total ?? 0;

            return parameterLinkCount > 0 && parameter.fieldApiName
              ? toDashboardFilter(
                  parameter,
                  suggestions,
                  linkCountsAndWarningsByParameterId,
                  globalFilterValues,
                  fieldsByName,
                  i18nLocale
                )
              : toUnlinkedDashboardFilter(parameter);
          }),
          fieldsByName,
        };
      }
    ),
    startWith({ dashboardFilters: [], fieldsByName: {} })
  );
